import { useState } from "react";
import { api } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { PageHeader, LoadingState, EmptyState, ErrorState } from "@/components/features/states";
import { formatRelativeTime, formatPrice } from "@/lib/format";
import { toast } from "sonner";
import { Flag, Check, ScrollText } from "lucide-react";
import { useTranslation } from "react-i18next";

const REASON_KEYS: Record<string, string> = {
  item_damaged: "admin.disputes.reasonItemDamaged",
  not_delivered: "admin.disputes.reasonNotDelivered",
  overcharged: "admin.disputes.reasonOvercharged",
  other: "admin.disputes.reasonOther",
};

export function AdminDisputes() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [resolving, setResolving] = useState<any | null>(null);
  const [resolution, setResolution] = useState("");
  const [finalStatus, setFinalStatus] = useState("completed");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "disputes"],
    queryFn: () => api.admin.disputes(),
    refetchInterval: 15000,
  });
  const items = data?.data?.items ?? [];

  const doResolve = async () => {
    if (!resolving || !resolution.trim()) return toast.error(t("admin.disputes.enterResolution"));
    const res = await api.admin.resolveDispute(resolving.id, { resolution: resolution.trim(), finalStatus });
    if (!res.ok) return toast.error(res.error);
    toast.success(t("admin.disputes.disputeResolvedToast"));
    setResolving(null);
    setResolution("");
    qc.invalidateQueries({ queryKey: ["admin", "disputes"] });
    qc.invalidateQueries({ queryKey: ["admin", "stats"] });
  };

  if (isLoading) return <LoadingState />;
  if (error || !data?.ok) return <ErrorState message={data?.error || t("admin.disputes.loadFailed")} onRetry={refetch} />;

  return (
    <div className="space-y-4">
      <PageHeader title={t("admin.disputes.title")} subtitle={t("admin.disputes.subtitle", { count: items.length })} />

      {items.length === 0 ? (
        <EmptyState
          icon={<ScrollText className="h-8 w-8" />}
          title={t("admin.disputes.empty")}
          description={t("admin.disputes.emptyDesc")}
        />
      ) : (
        <div className="space-y-3">
          {items.map((d: any) => (
            <Card key={d.id}>
              <CardContent className="space-y-3 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <Flag className="h-4 w-4 text-red-600" />
                      <Badge variant="outline" className="capitalize status-disputed border-none">{d.status}</Badge>
                      <span className="text-xs text-muted-foreground">{formatRelativeTime(d.createdAt)}</span>
                    </div>
                    <p className="mt-1 font-semibold">{REASON_KEYS[d.reason] ? t(REASON_KEYS[d.reason]) : d.reason}</p>
                  </div>
                  {d.job && (
                    <div className="text-right text-xs text-muted-foreground">
                      <p className="font-mono">{d.job.code}</p>
                      <p>{formatPrice(d.job.agreedPrice ?? d.job.customerOffer)}</p>
                    </div>
                  )}
                </div>
                <p className="rounded-lg bg-muted/40 p-3 text-sm">{d.description}</p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span>{t("admin.disputes.raisedBy")}: <span className="font-medium text-foreground">{d.raisedBy?.name}</span></span>
                  {d.job && (
                    <>
                      <span>·</span>
                      <span>{t("admin.disputes.customer")}: <span className="font-medium text-foreground">{d.job.customer?.name}</span></span>
                      <span>·</span>
                      <span>{t("admin.disputes.courier")}: <span className="font-medium text-foreground">{d.job.provider?.name || "—"}</span></span>
                    </>
                  )}
                </div>
                {d.status !== "resolved" ? (
                  <Button className="w-full" onClick={() => setResolving(d)}>
                    <Check className="mr-1 h-4 w-4" /> {t("admin.disputes.resolveDispute")}
                  </Button>
                ) : (
                  <div className="rounded-lg bg-emerald-50 p-3 text-sm dark:bg-emerald-950/30">
                    <p className="font-semibold text-emerald-800 dark:text-emerald-200">{t("admin.disputes.resolved")}</p>
                    <p className="text-emerald-700 dark:text-emerald-300">{d.resolution}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!resolving} onOpenChange={(o) => !o && setResolving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.disputes.resolveDispute")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t("admin.disputes.resolutionOutcome")}</Label>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setFinalStatus("completed")} className={`rounded-lg border p-2 text-sm ${finalStatus === "completed" ? "border-primary bg-primary/5" : "border-border"}`}>
                  {t("admin.disputes.markJobCompleted")}
                </button>
                <button onClick={() => setFinalStatus("cancelled")} className={`rounded-lg border p-2 text-sm ${finalStatus === "cancelled" ? "border-primary bg-primary/5" : "border-border"}`}>
                  {t("admin.disputes.cancelJob")}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="res">{t("admin.disputes.resolutionNotes")}</Label>
              <Textarea id="res" rows={3} placeholder={t("admin.disputes.resolutionNotesPlaceholder")} value={resolution} onChange={(e) => setResolution(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolving(null)}>{t("common.cancel")}</Button>
            <Button onClick={doResolve}>{t("admin.fraudFlags.resolveButton")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
